import { useReducer, useState, useEffect, useMemo } from 'react'
import Big from 'big.js'

import csrfHeaders from 'src/utils/csrfHeaders'

import { genericError } from './useRecord'

// State machine for the crypto-amount step. Local state for both
// fiat and crypto sides — the donor's broader-than-partner-list
// currency choice doesn't have to round-trip through useDonation,
// which is pinned to the partner-configured currency set.
//
// State sync is ONE-WAY (manager-decided 2026-06-01): parent
// `donation` changes propagate INTO useCrypto (Start-page edits
// reflect on the crypto step), but crypto-side edits never push
// back. Start's view of donation.state stays sacrosanct while the
// donor is exploring the crypto step.
//
// On submit, useCrypto overrides currency / donation_amount /
// amount_entered on the saveDraft via orderProps so the order's
// stored fiat values reflect the donor's actual crypto-step
// entries regardless of donation's parent-currency-constrained
// state. Contact's `collectAddress` $5000 trigger is handled by
// the cryptoDonation wrapper in CryptoForm, which reads from
// useCrypto's local state for amount/currency/usdAmount.

const reducer = (state, action) => {
  switch (action.type) {
  case 'setFiatCurrency':
    return { ...state, fiat_currency: action.value }
  case 'setCryptoCurrency':
    return { ...state, crypto_currency: action.value }
  case 'setSideAmount': {
    // Donor edit on one of the two inputs. Sets `mode` to the typed
    // side so the rate-change effect knows which side to keep stable
    // when it later recomputes the converted side via 'rederiveConverted'.
    const { side, value, exchangeRate, fiatExponent } = action
    return side === 'fiat'
      ? {
        ...state,
        mode: 'fiat',
        fiat_amount: value,
        crypto_amount: Big(value || 0).times(exchangeRate).round(8).toString(),
      }
      : {
        ...state,
        mode: 'crypto',
        crypto_amount: value,
        fiat_amount: Big(value || 0).div(exchangeRate)
          .round(fiatExponent).toString(),
      }
  }
  case 'syncFromDonation': {
    // One-way reseed from parent donation. Sets mode to 'fiat' (a
    // Start-page edit implies fiat-side intent); the rederiveConverted
    // effect downstream (deps on fiatCurrency + exchangeRate) handles
    // recomputing crypto_amount once the rate poll settles on the
    // new currency pair.
    return {
      ...state,
      mode: 'fiat',
      fiat_amount: action.fiat_amount,
      fiat_currency: action.fiat_currency,
    }
  }
  case 'rederiveConverted': {
    // Refresh only the converted side from the active side's current
    // value — used when the exchange rate or fiat currency churns
    // (poll tick or currency picker change). The donor's typed side
    // (state.mode) is preserved; the other side recomputes.
    const { exchangeRate, fiatExponent } = action
    return state.mode === 'fiat'
      ? {
        ...state,
        crypto_amount: Big(state.fiat_amount || 0).times(exchangeRate).round(8).toString(),
      }
      : {
        ...state,
        fiat_amount: Big(state.crypto_amount || 0).div(exchangeRate)
          .round(fiatExponent).toString(),
      }
  }
  default:
    throw new Error(`Invalid useCrypto action: ${action.type}`)
  }
}

const useCrypto = ({
  i18n, donation, record, partner, partnerItem, projectId,
  cryptoProps, currencies, donationTags, embeddedOn, originalFundraiserId,
  setSubmitInFlight, enabled,
}) => {
  // Called unconditionally at the App level even on partners without
  // crypto configured (cryptoProps falsy). State sets up with safe
  // defaults; effects skip when `enabled` is false; sendPayment is
  // only reachable when the donor's on the crypto path.
  const [ exchangeRate, setExchangeRate ] = useState(cryptoProps?.exchange_rate || 0)
  const [ stepTwo, setStepTwo ] = useState(null)
  const [ errors, setErrors ] = useState(null)
  // The currency pair the current `exchangeRate` was fetched for. The
  // seeded rate (cryptoProps.exchange_rate) is for the initial pair.
  // When the donor changes either currency, the rate is stale until
  // the poll returns a fresh one for the new pair — and if that fetch
  // fails, it stays stale. `rateStale` (below) gates submit so a
  // mismatched crypto_amount (new asset, old asset's rate) can't be
  // sent.
  const [ ratePair, setRatePair ] = useState({
    fiat: donation.currency.iso_code,
    crypto: cryptoProps?.crypto_currency || '',
  })

  // Seed fiat from the parent so Start's amount + currency pre-fill
  // the crypto step. From here, both sides live locally until commit.
  const [ state, dispatch ] = useReducer(reducer, null, () => ({
    mode: 'fiat',
    fiat_currency: donation.currency.iso_code,
    fiat_amount: donation.state.amount,
    crypto_currency: cryptoProps?.crypto_currency || '',
    crypto_amount: Big(donation.state.amount || 0)
      .times(cryptoProps?.exchange_rate || 0).round(8).toString(),
  }))

  const fiatCurrency = useMemo(
    () => currencies.find(c => c.iso_code === state.fiat_currency),
    [ currencies, state.fiat_currency ],
  )

  // Live exchange-rate poll. Stops once `stepTwo` arrives — once the
  // address is generated the donor's quoted crypto amount mustn't
  // shift underneath them. Re-runs when either currency changes so
  // the active pair always has a current rate. Gated on `enabled` so
  // we don't burn requests for fiat-only donors at crypto-enabled
  // partners — App flips it on when the donor picks crypto.
  useEffect(() => {
    if (stepTwo || !enabled) return

    // Ignore any in-flight response once this effect run is superseded
    // (currency changed → cleanup) or unmounted. Without it, rapid
    // A→B→C switching where B resolves after C would stamp ratePair
    // back to B while state is C, leaving rateStale true (button stuck
    // disabled) until the next 30s tick re-fetched C. Never corrupts
    // (the rateStale guard blocks submit either way) — this just keeps
    // a superseded pair's response from clobbering the current one.
    let cancelled = false
    const update = () => {
      const url = `${cryptoProps.exchange_rate_url}?${new URLSearchParams({
        fiat_currency: state.fiat_currency,
        crypto_currency: state.crypto_currency,
      })}`
      fetch(url, { headers: { Accept: 'application/json' } })
        .then(r => r.json())
        .then(json => {
          if (cancelled) return
          setExchangeRate(json.exchange_rate)
          // Stamp the pair this rate is good for so rateStale clears
          // only once the fresh quote for the selected pair lands.
          setRatePair({ fiat: state.fiat_currency, crypto: state.crypto_currency })
        })
        .catch(console.error)
    }

    update()
    const interval = setInterval(update, 30000)
    return () => { cancelled = true; clearInterval(interval) }
  }, [ stepTwo, enabled, state.fiat_currency, state.crypto_currency, cryptoProps?.exchange_rate_url ])

  // Re-derive the converted side whenever the rate or fiat currency
  // changes — the donor's "active" input keeps its typed value, the
  // other side recomputes against the new rate / exponent.
  useEffect(() => {
    dispatch({
      type: 'rederiveConverted',
      exchangeRate,
      fiatExponent: fiatCurrency.exponent,
    })
  }, [ exchangeRate, fiatCurrency ])

  // One-way sync from parent donation → useCrypto. Fires when the
  // donor changes amount or currency on Start (or when `enabled`
  // flips on as the donor enters the crypto path), seeding local
  // state from donation. Crypto-side edits never trigger this
  // effect (they only mutate local state), so donation stays
  // untouched by the crypto step.
  useEffect(() => {
    if (!enabled) return
    dispatch({
      type: 'syncFromDonation',
      fiat_amount: donation.state.amount,
      fiat_currency: donation.currency.iso_code,
    })
  }, [ enabled, donation.state.amount, donation.currency.iso_code ])

  // Per-side handlers for the unified CryptoForm layout where both
  // inputs are visible simultaneously. Each sets mode to the typed
  // side so cross-conversion on rate change recomputes the other.
  const handleFiatAmountChange = e => dispatch({
    type: 'setSideAmount',
    side: 'fiat',
    value: e.target.value,
    exchangeRate,
    fiatExponent: fiatCurrency.exponent,
  })

  const handleCryptoAmountChange = e => dispatch({
    type: 'setSideAmount',
    side: 'crypto',
    value: e.target.value,
    exchangeRate,
    fiatExponent: fiatCurrency.exponent,
  })

  const setFiatCurrency = value => dispatch({ type: 'setFiatCurrency', value })
  const setCryptoCurrency = value => dispatch({ type: 'setCryptoCurrency', value })

  // Two-step crypto submit, fired from CryptoForm's onSubmit. saveDraft
  // creates the pledged Order (donor info + Canadian receipt / Gift Aid
  // / tribute / data-sharing extras are all handled by useRecord); the
  // crypto_donations#create call attaches a CryptoInput and returns the
  // wallet address + network + destination_tag for the crypto_info step.
  //
  // The orderProps override channel (useRecord spreads `orderProps`
  // last) lets us replace donation's `currency` / `donation_amount`
  // with crypto's local values — necessary because donation never sees
  // the crypto step's edits per the one-way sync. currency takes the
  // broader-list id (fiatCurrency.id), since the donor's chosen
  // currency may be outside the partner-configured set. `amount_entered`
  // is intentionally NOT overridden: useRecord nulls it whenever
  // principalOnly is true (the donor's typed fiat is aspirational for
  // crypto — rates can drift, donor can send a different amount, etc.).
  // True while the shown quote doesn't match the selected pair — right
  // after a currency change (poll pending) or when that fetch failed.
  const rateStale =
    ratePair.fiat !== state.fiat_currency ||
    ratePair.crypto !== state.crypto_currency

  const sendPayment = async () => {
    // Guard beyond the disabled button: never submit a crypto_amount
    // derived from a stale rate for a different asset.
    if (rateStale) return { errors: genericError(i18n) }
    setErrors(null)
    setSubmitInFlight(true)

    try {
      const { errors: draftErrors, token: orderToken } = await record.saveDraft({
        principalOnly: true,
        order: {
          payment_source:  'crypto',
          currency:        fiatCurrency.id,
          donation_amount: state.fiat_amount,
        },
      })
      if (draftErrors) {
        setSubmitInFlight(false)
        setErrors(draftErrors)
        return { errors: draftErrors }
      }

      const response = await fetch(cryptoProps.submitUrl, {
        method: 'POST',
        headers: csrfHeaders(),
        body: JSON.stringify({
          order_id: orderToken,
          first_name: donation.state.firstName,
          last_name: donation.state.lastName,
          email: donation.state.email,
          phone: donation.state.phone,
          donor_address: donation.state.address,
          partner_key: partner.key,
          external_id: partnerItem.external_id,
          project_id: projectId,
          organization_id: partnerItem.beneficiary.id,
          original_fundraiser_id: originalFundraiserId,
          payment_widget_config_id: cryptoProps.widget_id,
          fiat_currency: state.fiat_currency,
          fiat_amount: state.fiat_amount,
          crypto_currency: state.crypto_currency,
          crypto_amount: state.crypto_amount,
          // Computed from crypto's local state — donation.usdAmount
          // still reflects Start's amount/currency per the one-way
          // sync, so it's not the right source here.
          usd_amount: Big(state.fiat_amount || 0).div(fiatCurrency.rate).round(2).toString(),
          shares: donation.shares,
          donation_tags: donationTags,
          embedded_on: embeddedOn,
        }),
      })

      const json = await response.json()
      if (response.ok) {
        // Drop the overlay before crypto_info mounts — that panel
        // shows the QR + polls for status, and the donor needs to
        // interact with it (copy button, etc.). Leaving the overlay
        // up would lock the entire panel area.
        setSubmitInFlight(false)
        setStepTwo(json)
        return { stepTwo: json }
      }

      setSubmitInFlight(false)
      const errs = json.errors || genericError(i18n)
      setErrors(errs)
      return { errors: errs }
    } catch (error) {
      console.error(error)
      setSubmitInFlight(false)
      setErrors(genericError(i18n))
      return { errors: genericError(i18n) }
    }
  }

  // Clear the once-only crypto-side state so a second donation from
  // the same widget session can mint a fresh address. Keeps `state`
  // (amount + currency selections) intact — the donor likely wants
  // the same values for round two.
  const reset = () => {
    setStepTwo(null)
    setErrors(null)
  }

  return {
    state, fiatCurrency, exchangeRate, stepTwo, errors, rateStale,
    handleFiatAmountChange, handleCryptoAmountChange,
    setFiatCurrency, setCryptoCurrency,
    sendPayment, reset,
  }
}

export default useCrypto
